import { classImplementsInterfaces } from "@goodgamestudios/createjs-utilities";

import { TextFieldHelper } from "@goodgamestudios/core";
import { BasicModel } from "@goodgamestudios/core";
import { BasicController } from "@goodgamestudios/core";
import { Timer } from "@goodgamestudios/createjs-utilities";
import { Sine } from "gsap";
import { SFConstants } from "../../../constants/SFConstants";
import { Constants } from "../../../constants/shared/Constants";
import { Poker2LobbyDetailEvent } from "../../../event/Poker2LobbyDetailEvent";
import { Poker2TournamentDataEvent } from "../../../event/Poker2TournamentDataEvent";
import { AbstractLobbyDetailVO } from "../../../gamevo/AbstractLobbyDetailVO";
import { ItemsVO } from "../../../gamevo/ItemsVO";
import { TournamentInfoLineVO } from "../../../gamevo/TournamentInfoLineVO";
import { TournamentLobbyDetailVO } from "../../../gamevo/TournamentLobbyDetailVO";
import { Poker2Model } from "../../../model/Poker2Model";
import { DispHelper } from "../../helper/DispHelper";
import { ItemHelper } from "../../helper/ItemHelper";
import { Poker2TextFieldHelper } from "../../helper/Poker2TextFieldHelper";
import { PokerTextHelper } from "../../helper/PokerTextHelper";
import { ILobbyDetailComponent } from "./ILobbyDetailComponent";

import Container = createjs.Container;
import MovieClip = createjs.MovieClip;
import MouseEvent = createjs.MouseEvent;
import TimerEvent = createjs.TimerEvent;
import { TweenMaxGroup } from "../../helper/TweenMaxGroup";

export class TournamentLobbyDetailComponent implements ILobbyDetailComponent {
    private UPDATE_DELAY: number = 30000;
    private NUM_PAYOUTS: number = 8;

    private TABLE_Y_HAS_SPECIAL_ITEM: number = 136.75;
    private TABLE_Y_HAS_NO_SPECIAL_ITEM: number = 77;

    private _holder: Container;
    private _view: Library.PokerInterface.LobbyTournamentDetailView;
    private _updateTimer: Timer;
    private _currentPage: number = 0;
    private _vo: TournamentLobbyDetailVO;
    private _tsg_fadeContent: TweenMaxGroup;
    private _contentMc: MovieClip;

    constructor(holder: Container, vo: TournamentLobbyDetailVO) {
        this._holder = holder;
        this._vo = vo;
        this._view = new Library.PokerInterface.LobbyTournamentDetailView();
        this._view.changeTextFieldCacheScale(2);

        this._contentMc = this._view.mc_content;
        this.resetContentMc();
        this._view.bg.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);
        this._view.btn_refresh.toolTipText = Poker2Model.languageData.getTextById("generic_btn_refresh");

        // show content defined within the vo immediately without transition
        this.updateContent(false);

        // data will be updated automatically
        this.startUpdateTimer();

        // enable component
        this.enable();
    }

    private resetContentMc(): void {
        this._contentMc.alpha = 1;
        this._contentMc.mc_info.gotoAndStop(1);
        this._contentMc.mc_logos.gotoAndStop(1);
    }

    // tournament info update timer -> updates the tournament table every 30 seconds
    private startUpdateTimer(): void {
        this.stopUpdateTimer();
        // TODO: auto update of tournament info deactivated. Problem: is should be detected wether the tournament is still active.
        //			_updateTimer = new Timer(UPDATE_DELAY);
        //			_updateTimer.addEventListener( TimerEvent.TIMER, updatePayout );
        //			_updateTimer.start();
    }

    private updatePayout(event: TimerEvent): void {
        this.updateTournamentInfo();
    }

    private updateTournamentInfo(): void {
        var paramObject: any = { id: this._vo.id, update: true };
        BasicModel.smartfoxClient.sendMessage("jio", [JSON.stringify(paramObject)]);
    }

    protected onClick(event: MouseEvent): void {
        switch (event.target.name) {
            case "btn_close":

                this.stopUpdateTimer();
                this.disposeTweenGroup(this._tsg_fadeContent);
                this._view.dispatchEvent(new Poker2LobbyDetailEvent("hideDetail", null, true));
                BasicController.getInstance().dispatchEvent(new Poker2TournamentDataEvent("blurSelectedTableLine"));
                break;

            case "btn_refresh":

                this.updateTournamentInfo();
                break;
        }
    }

    public resetView(): void {
        this.disposeTweenGroup(this._tsg_fadeContent);
        this.resetContentMc();
    }

    public enable(): void {
        this._view.btn_close.addEventListener(MouseEvent.CLICK, this.bindFunction(this.onClick));
        this._view.btn_refresh.addEventListener(MouseEvent.CLICK, this.bindFunction(this.onClick));
        this.startUpdateTimer();
    }

    public disable(): void {
        this._view.btn_close.removeEventListener(MouseEvent.CLICK, this.bindFunction(this.onClick));
        this._view.btn_refresh.removeEventListener(MouseEvent.CLICK, this.bindFunction(this.onClick));
        this.stopUpdateTimer();
    }

    public destroy(): void {
        this.disposeTweenGroup(this._tsg_fadeContent);
        this.disable();
        this._view = null;
        this._vo = null;
    }

    private stopUpdateTimer(): void {
        if (this._updateTimer) {
            this._updateTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.updatePayout));
            this._updateTimer.stop();
            this._updateTimer = null;
        }
    }

    public update(lobbyDetailVO: AbstractLobbyDetailVO, transition: boolean): void {
        this._vo = lobbyDetailVO as TournamentLobbyDetailVO;
        this.updateContent(transition);
    }

    private updateContent(transition: boolean = false): void {
        if (transition) {
            this.fadeOutContent();
        }
        else {
            // update view
            this.updateTexts();
            this.drawInfoLines();
        }
    }

    private fadeOutContent(): void {
        this.disable();
        this._contentMc.cacheAsBitmap = true;
        this._tsg_fadeContent = new TweenMaxGroup();
        this._tsg_fadeContent.to(this._contentMc, 0.2, { alpha: 0, ease: Sine.easeOut, onComplete: this.bindFunction(this.onFadeOutContentComplete) });
        this._tsg_fadeContent.play();
    }

    private onFadeOutContentComplete(): void {
        this.disposeTweenGroup(this._tsg_fadeContent);

        // update view
        this.updateTexts();
        this.drawInfoLines();

        // fade in content
        this.fadeInContent();
    }

    private fadeInContent(): void {
        this._tsg_fadeContent = new TweenMaxGroup();
        this._tsg_fadeContent.to(this._contentMc, 0.3, { alpha: 1, ease: Sine.easeIn, onComplete: this.bindFunction(this.onFadeInContentComplete) });
        this._tsg_fadeContent.play();
    }

    private onFadeInContentComplete(): void {
        this.enable();
        this._contentMc.cacheAsBitmap = false;
        this.disposeTweenGroup(this._tsg_fadeContent);
    }

    private updateTexts(): void {
        var tableY: number = NaN;

        // special item check
        if (this._vo.itemId == -1) {
            // no special item
            this._contentMc.mc_info.gotoAndStop(1);
            this._contentMc.mc_info.changeTextFieldCacheScale(2);

            // update table position
            tableY = this.TABLE_Y_HAS_NO_SPECIAL_ITEM;
        }
        else {
            // special item
            this._contentMc.mc_info.gotoAndStop(2);
            this._contentMc.mc_info.changeTextFieldCacheScale(2);

            // item view
            this.drawSpecialItem();

            // item label
            this._contentMc.mc_info.txt_itemName.text = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_1");

            // update table position
            tableY = this.TABLE_Y_HAS_SPECIAL_ITEM;
        }

        // set table y position
        this._contentMc.mc_payOutTable.y = tableY;

        // header
        Poker2TextFieldHelper.changeTextFromatSizeByMaxHeight(9, this._contentMc.mc_header.txt_headline, Poker2Model.languageData.getTextById("lobby_detailedInfo_title") + " - " + this._vo.name);

        // state label
        TextFieldHelper.changeTextFromatSizeByTextWidth(9, this._contentMc.mc_info.txt_state_label, Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0"));

        // state description
        var stateDescription: string = this.getStatusDescription(this._vo.state, this._vo.rank, this._vo.playerCount);
        TextFieldHelper.changeTextFromatSizeByTextWidth(9, this._contentMc.mc_info.txt_state_value, stateDescription, 2);

        // pot label
        TextFieldHelper.changeTextFromatSizeByTextWidth(9, this._contentMc.mc_info.txt_winpot_label, Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_2"));

        // pot chips
        var potChips: number = (isNaN(this._vo.potChips)) ? 0 : this._vo.potChips;
        this._contentMc.mc_info.txt_potChips_value.text = PokerTextHelper.getShortGroupNumberString(potChips);

        // pot gold
        var potGold: number = (isNaN(this._vo.potGold)) ? 0 : this._vo.potGold;
        this._contentMc.mc_info.txt_potGold_value.text = PokerTextHelper.getShortGroupNumberString(potGold);

        // TODO: pointsMultiplier
        //			var pointsMultiplier:Number = (isNaN(_vo.pointsMultiplier)) ? 0 :_vo.pointsMultiplier;
        //			_contentMc.mc_info.txt_pointsMultiplier_value.text = pointsMultiplier;

        // update logo
        this._contentMc.mc_logos.gotoAndStop(this.getLogoFrameByTournamentType(this._vo.type));
    }

    private getLogoFrameByTournamentType(type: number): any {
        var frame: number = 0;

        switch (type) {
            case 0:
            case 1:
            case 2:
            case 3:

                // 0 = shootout
                // 1 = medium shootout
                // 2 = highroller shootout
                // 3 = freeroller shootout
                frame = 4;
                break;

            case 4:
            case 5:
            case 6:

                // 4 = freezeout
                // 5 = medium freezeout
                // 6 = highroller freezeout
                frame = 5;
                break;

            case 8:

                // 8 = rebuy shootout
                frame = 7;
                break;

            default:

                frame = 1;
        }

        return frame;
    }

    private drawSpecialItem(): void {
        var itemVO: ItemsVO = Poker2Model.wodData.getItemVObyID(this._vo.itemId) as ItemsVO;
        var itemMC: Container = AvatarHelper.drawItem(itemVO);
        var itemHolder: MovieClip = this._contentMc.mc_info.mc_itemHolder as MovieClip;

        // reset holder
        DispHelper.removeAllChildrenOf(itemHolder);

        // add new item
        itemHolder.addChild(itemMC);

        // scale item
        ItemHelper.scaleItem(itemMC);
    }

    /**
     *  Maps player state to the correct text to display.
     *  0 = tournament not running, player is not registered
     *  1 = tournament not running, player is registered
     *  2 = tournament is running, player is not registered
     *  3 = tournament is running, player is registered
     *  4 = tournament is running, player is registered and player is disqualified
     **/
    private getStatusDescription(state: number, rank: number, playerCount: number): string {
        var stateDescription: string;

        switch (state) {
            case 0:

                // 'lobby_detailedInfo_copy_0_1 -> name='Angemeldet'
                stateDescription = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_2");
                break;

            case 1:

                // 'lobby_detailedInfo_copy_0_2 -> name='Nicht angemeldet'
                stateDescription = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_1");
                break;

            case 2:

                // 'lobby_detailedInfo_copy_0_3 -> name='Nicht teilgenommen'
                stateDescription = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_3");
                break;

            case 3:
            case 5:

                // 'lobby_detailedInfo_copy_0_4 -> name='Noch dabei
                // 'lobby_detailedInfo_copy_0_6' -> Platz {0}/{1}'
                stateDescription = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_4") + " \n" + Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_6", [rank.toString(), playerCount.toString()]);
                break;

            case 4:

                // 'lobby_detailedInfo_copy_0_5 -> name='Ausgeschieden'
                // 'lobby_detailedInfo_copy_0_6' -> Platz {0}/{1}'
                stateDescription = Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_5") + " \n" + Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_0_6", [rank.toString(), playerCount.toString()]);
                break;

            default:

                stateDescription = "";
        }

        return stateDescription;
    }

    private drawInfoLines(): void {
        var startPos: number = this._currentPage * this.NUM_PAYOUTS;
        var infoLineArray: TournamentInfoLineVO[] = this._vo.payOut;

        //			initArrows( infoLineArray.length );

        var i: number = 0;
        var len: number = startPos + this.NUM_PAYOUTS;
        for (i = startPos; i < len; ++i) {
            var currentItem: number = i - startPos;
            var infoLine: Library.PokerInterface.TournamentInfoLine = this._contentMc.mc_payOutTable["line" + currentItem] as Library.PokerInterface.TournamentInfoLine;

            if (i < infoLineArray.length) {
                var infoLineVO: TournamentInfoLineVO = infoLineArray[i];
                infoLine.mouseChildren = false;

                // rank
                TextFieldHelper.changeTextFromatSizeByTextWidth(9, infoLine.txt_tables_0, infoLineVO.rank.toString());
                // chips
                infoLine.txt_tables_2.text = PokerTextHelper.getShortGroupNumberString(infoLineVO.chips);
                // gold
                TextFieldHelper.changeTextFromatSizeByTextWidth(9, infoLine.txt_tables_1, infoLineVO.gold.toString());

                infoLine.visible = true;
            }
            else {
                infoLine.visible = false;
            }
        }

        if (infoLineArray.length == 0) {
            this._contentMc.mc_payOutTable.mc_tableHeader.visible = false;
            return;
        }

        // table header text
        this._contentMc.mc_payOutTable.mc_tableHeader.visible = true;
        Poker2TextFieldHelper.changeTextFromatSizeByMaxHeight(9, this._contentMc.mc_payOutTable.mc_tableHeader.txt_headline, Poker2Model.languageData.getTextById("lobby_detailedInfo_copy_3"));
    }

    public disposeTweenGroup(tween: TweenMaxGroup): void {
        if (!tween) {
            return;
        }

        tween.destroy();
        tween = null;
    }

    public get view(): Container {
        return this._view;
    }
}
import { AvatarHelper } from "../../helper/AvatarHelper";
import {Poker2LayoutManager} from "../../Poker2LayoutManager";
classImplementsInterfaces(TournamentLobbyDetailComponent, "ILobbyDetailComponent");
